import {
   ContactTemplateModel,
   ContactTemplatesStore,
   GOFResponseWithFieldResponse,
   SystemFieldEnum,
   RecordUtils,
   StageStore,
   UserStore,
   TemplateDetailsStatusEnum,
   DealStore,
   DealModel,
   DealTemplateType,
   DealTemplatesStore,
   FieldsStore,
   GOFResponse,
   UnionFieldModelType,
   DealTemplateModel,
   Stage
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   AddAnotherGoFResult,
   FieldResponse,
   SystemFieldEnumResponseResult
} from '@flowwai-frontend/bps-officer-web/graphql'

import { Common_PipelineItemTemplate_FragmentFragment } from '../fragments/pipelineItemTemplateFragment.generatedTypes'
import { SystemFieldResponsesApiData } from '../types'
import { getTransformedFieldResponse } from '../utils/FieldResponseUtils'
import { convertToGoFWithFieldsType } from '../utils/TransformationUtils/TransformationUtils'

import { convertGraphqlFieldResponseToUnionFieldResponse } from './fields'

interface CreateDealModelProps {
   contactTemplatesStore: ContactTemplatesStore
   dealId: string
   dealStore: DealStore
   dealTemplateId: string
   systemFieldsResponse: SystemFieldResponsesApiData
}

interface StoreDealFieldResponsesInDealMethodProps {
   fieldResponse: SystemFieldEnumResponseResult
   dealModel: DealModel
   contactTemplate: ContactTemplateModel
   userStore: UserStore
   stageStore: StageStore
}

export const storeDealAssigneeFieldResponseInUserStore = (
   transformedFieldResponse: any,
   userStore: UserStore,
   fieldResponse: FieldResponse
) => {
   const isFieldDealAssigneeField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.DealAssignee

   if (isFieldDealAssigneeField) {
      userStore.createOrUpdateUser({
         ...transformedFieldResponse.response
      })
   }
}

export const setFieldResponseInDealModel = (
   field: UnionFieldModelType,
   dealModel: DealModel,
   stageStore: StageStore,
   fieldResponse: FieldResponse,
   userStore: UserStore,
   gofResponseId?: string
) => {
   if (!field.gof) throw new Error('Gof is not found for field')

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      field,
      fieldResponse
   )

   const gofId = field.gof.gofId
   const dummyGofResponseId = RecordUtils.getGoFResponseId(
      dealModel.dealId,
      gofId
   )

   const isDealStage = field.systemFieldEnum === SystemFieldEnum.DealStage
   if (isDealStage)
      stageStore.createOrUpdateStage({
         ...(transformedFieldResponse.response as Stage)
      })

   dealModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId || dummyGofResponseId,
      transformedFieldResponse
   )

   storeDealAssigneeFieldResponseInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )
}

const storeDealStageInStageStore = (
   transformedFieldResponse: any,
   stageStore: StageStore,
   fieldResponse: FieldResponse
) => {
   const isDealStageField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.DealStage

   if (isDealStageField) {
      stageStore.createOrUpdateStage({
         ...transformedFieldResponse.response
      })
   }
}

export const storeDealModelInDealsStore = (
   props: CreateDealModelProps
): DealModel => {
   const {
      contactTemplatesStore,
      dealId,
      dealStore,
      systemFieldsResponse,
      dealTemplateId
   } = props

   const contactTemplateModel = contactTemplatesStore.templates.find(
      template => template instanceof ContactTemplateModel
   )
   if (!contactTemplateModel) {
      throw new Error('contactTemplateModel not found')
   }

   const gofResponses: Array<GOFResponseWithFieldResponse> = []
   const { contactId } = systemFieldsResponse

   if (!contactId) {
      throw new Error('contactId is not available')
   }

   const contact = {
      contactId: contactId,
      recordId: contactId,
      templateId: contactTemplateModel.templateId,
      gofResponses: gofResponses
   }

   const deal = {
      dealId: dealId,
      contact: contact,
      recordId: dealId,
      templateId: dealTemplateId,
      gofResponses: gofResponses
   }
   dealStore.createOrUpdateDeal(deal)
   return dealStore.getDeal(dealId)
}

export const storeDealFieldResponsesInDeal = (
   props: StoreDealFieldResponsesInDealMethodProps
) => {
   const {
      fieldResponse,
      userStore,
      contactTemplate,
      dealModel,
      stageStore
   } = props

   const isFieldResponseType = fieldResponse.__typename === 'FieldResponse'
   if (!isFieldResponseType) return

   const dealTemplateField = dealModel.dealTemplate.fields.find(
      field => field.fieldId === fieldResponse.fieldId
   )
   const fieldModel =
      dealTemplateField ?? contactTemplate.getField(fieldResponse.fieldId)

   if (!fieldModel.gof) {
      throw new Error(`${fieldModel.fieldId}: GOF is undefined in the Field`)
   }
   const gofId = fieldModel.gof.gofId

   const gofResponseId = RecordUtils.getGoFResponseId(
      dealModel.contactId,
      gofId
   )

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      fieldModel,
      fieldResponse
   )

   storeDealAssigneeFieldResponseInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )

   storeDealStageInStageStore(
      transformedFieldResponse,
      stageStore,
      fieldResponse
   )

   dealModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId,
      transformedFieldResponse
   )
}

export const convertDealTemplateResponseToDealTemplateType = (
   dealTemplateResponse: Common_PipelineItemTemplate_FragmentFragment
): DealTemplateType => {
   const {
      pipelineItemTemplateId,
      gofs,
      assigneeFieldsMapping
   } = dealTemplateResponse

   return {
      templateId: pipelineItemTemplateId,
      name: dealTemplateResponse.name,
      gofs: gofs.map(convertToGoFWithFieldsType),
      assigneeFieldsMapping: assigneeFieldsMapping,
      workflows: []
   }
}

export const storeDealTemplateModelInTemplateStore = (
   dealTemplateFragmentData: Common_PipelineItemTemplate_FragmentFragment,
   dealTemplateStore: DealTemplatesStore
) => {
   const templateId = dealTemplateFragmentData.pipelineItemTemplateId
   const dealTemplateModelInput: DealTemplateType = convertDealTemplateResponseToDealTemplateType(
      dealTemplateFragmentData
   )

   dealTemplateStore.createOrUpdateTemplate(dealTemplateModelInput)
   dealTemplateStore
      .getTemplate(templateId)
      .setTemplateDetailsStatus(
         TemplateDetailsStatusEnum.FetchedCompleteDetails
      )

   dealTemplateStore
      .getTemplate(templateId)
      .setCanMergeRecords(dealTemplateFragmentData.canMergeRecords)
}

export const storeAddAnotherGofResponsesInDealModel = (
   addAnotherGofResponses: AddAnotherGoFResult[],
   dealModel: DealModel,
   fieldsStore: FieldsStore
): void =>
   addAnotherGofResponses.forEach(gofResponseConfig => {
      gofResponseConfig.gofResponses.forEach(gofResponse => {
         const gofResponseData: GOFResponse = {
            gofId: gofResponse.gofId,
            gofResponseId: gofResponse.gofResponseId
         }

         dealModel.createOrUpdateGOFResponse(gofResponseData)

         gofResponse.fieldResponses.forEach(fieldResponse => {
            const fieldModel = fieldsStore.getField(fieldResponse.fieldId)

            const transformedFieldResponse = getTransformedFieldResponse(
               fieldModel,
               fieldResponse as FieldResponse
            )

            dealModel.createOrUpdateFieldResponse(
               gofResponse.gofId,
               gofResponse.gofResponseId,
               transformedFieldResponse
            )
         })
      })
   })

export const storeDealBaseDetailsInTemplateStore = (
   templateId: string,
   templateName: string,
   dealTemplateStore: DealTemplatesStore
): void => {
   dealTemplateStore.createOrUpdateTemplate({
      name: templateName,
      templateId,
      assigneeFieldsMapping: [],
      gofs: [],
      workflows: []
   })
}
